/**
 * Bouton de partage. Genere le PNG a la demande (jamais au montage : c'est
 * couteux et la plupart des joueurs ne partageront pas), puis partage nativement
 * ou telecharge.
 */
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useT } from '../locales/useT.js';
import { useRoomStore } from '../store/roomStore.js';
import { resolveArtwork } from './Gallery.js';
import { buildShareImage, shareOrDownload, socialIntents } from './shareImage.js';

type Status = 'idle' | 'working' | 'shared' | 'downloaded' | 'error';

export function ShareButton(): ReactElement | null {
  const t = useT();
  const gallery = useRoomStore((state) => state.gallery);
  const [status, setStatus] = useState<Status>('idle');

  const shareText = t('share.text', { name: t('app.title') });
  const intents = useMemo(() => socialIntents(shareText), [shareText]);

  const inputs = useMemo(() => {
    if (gallery === null) return [];
    return gallery.items
      .map((item) => resolveArtwork(item, gallery.drawings[item.slot] ?? null))
      .filter((input): input is NonNullable<typeof input> => input !== null);
  }, [gallery]);

  const share = useCallback(async () => {
    if (inputs.length === 0) return;
    setStatus('working');
    try {
      const blob = await buildShareImage({ title: t('app.title'), items: inputs });
      // Le texte SEUL : shareOrDownload passe l'URL dans le champ `url` de
      // navigator.share. La concatener ici la faisait apparaitre deux fois.
      const outcome = await shareOrDownload(blob, shareText);
      setStatus(outcome);
    } catch {
      setStatus('error');
    }
    window.setTimeout(() => setStatus('idle'), 3000);
  }, [inputs, shareText, t]);

  if (gallery === null) return null;

  const label =
    status === 'working'
      ? t('share.working')
      : status === 'shared'
        ? t('share.done')
        : status === 'downloaded'
          ? t('share.downloaded')
          : status === 'error'
            ? t('share.error')
            : t('share.button');

  return (
    <div className="share">
      <button
        type="button"
        className="button-ghost"
        disabled={status === 'working' || inputs.length === 0}
        onClick={() => void share()}
      >
        {label}
      </button>

      {/* Sur mobile navigator.share couvre tout ; ces liens sont le chemin desktop. */}
      <div className="share__intents">
        {intents.map((intent) => (
          <a
            key={intent.id}
            className="share__intent"
            href={intent.url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {intent.label}
          </a>
        ))}
      </div>
    </div>
  );
}
